import { getInputProps } from '@conform-to/react'
import { useState, useEffect } from 'react'
import {
  calculateDiscountedPrice,
  calculateFinancedAmount,
  formatPrice,
  getApplicableTIN,
} from '#app/shared/lib/price.ts'
import { Field } from '#app/shared/ui/fields.tsx'
import { Card } from '#app/shared/ui/surfaces/card.tsx'
import { WarrantyTypeEnum } from '../../../../../types/enums'

type Props = {
  fields: any
}

const MAX_FINANCING_MONTHS = 120
const MIN_FINANCING_MONTHS = 36
export const DEFAULT_ANNUAL_INTEREST_RATE = 6.99

const calculateFinancingOptions = (
  price: number,
  annualInterestRate: number,
  annualInterestRatePackGo: number,
) => {
  if (!price || annualInterestRate == null) return null

  // MAX TERM: 120 months
  // calculate applicable TIN for maximum term
  // Priority: 1) TIN pack, 2) TIN normal, 3) Default TIN
  const maxTermTin = getApplicableTIN(
    true,
    annualInterestRate ?? 0,
    annualInterestRatePackGo,
  )

  // calculate total debt
  const { quota: maxTermMonthly, total: maxTermTotalAmount } =
    calculateFinancedAmount({
      price,
      warrantyData: {
        // Price = 0
        type: WarrantyTypeEnum.BASIC,
        duration: 1,
      },
      initialEntry: 0,
      months: MAX_FINANCING_MONTHS,
      TIN: maxTermTin,
      hasPackOptima: true,
    })

  // MIN TERM: 36 months
  // calculate applicable TIN for minimum term
  const minTermTin = getApplicableTIN(
    false,
    annualInterestRate ?? 0,
    null, // Don't use pack for 36 months
  )

  // calculate monthly payment using PMT
  const { quota: minTermMonthly, total: minTermTotalAmount } =
    calculateFinancedAmount({
      price,
      warrantyData: {
        // Price = 0
        type: WarrantyTypeEnum.BASIC,
        duration: 1,
      },
      initialEntry: 0,
      months: MIN_FINANCING_MONTHS,
      TIN: minTermTin,
      hasPackOptima: false,
    })

  return {
    basePrice: price,
    maxTerm: {
      totalAmount: maxTermTotalAmount,
      monthlyPayment: maxTermMonthly
        ? Math.round(maxTermMonthly * 100) / 100
        : null,
      months: MAX_FINANCING_MONTHS,
      tin: maxTermTin,
    },
    minTerm: {
      totalAmount: minTermTotalAmount,
      monthlyPayment: minTermMonthly
        ? Math.round(minTermMonthly * 100) / 100
        : null,
      months: MIN_FINANCING_MONTHS,
    },
  }
}

export const ProductPrice = ({ fields }: Props) => {
  const [financing, setFinancing] = useState<any>(null)
  const [finalPrice, setFinalPrice] = useState<number | null>(null)
  const [finalFinancePrice, setFinanceFinalPrice] = useState<number | null>(
    null,
  )

  useEffect(() => {
    setFinalPrice(
      calculateDiscountedPrice(fields.price.value, fields.discount.value),
    )

    const annualInterestRate = parseFloat(fields.annualInterestRate.value) || 0
    const discountedFinancePrice = calculateDiscountedPrice(
      fields.minFinancedAmount.value,
      fields.discount.value,
    )
    const annualInterestRatePackGo =
      parseFloat(fields.annualInterestRatePackGo.value) || 0
    setFinanceFinalPrice(discountedFinancePrice)

    if (discountedFinancePrice && annualInterestRate) {
      const options = calculateFinancingOptions(
        discountedFinancePrice,
        annualInterestRate,
        annualInterestRatePackGo,
      )
      setFinancing(options)
    }
  }, [
    fields.minFinancedAmount.value,
    fields.discount.value,
    fields.annualInterestRate.value,
    fields.annualInterestRatePackGo.value,
  ])

  return (
    <Card className="flex flex-col gap-7 p-6">
      <h2 className="text-h2">Precio</h2>
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="w-full">
            <Field
              labelProps={{ children: 'Precio al contado' }}
              inputProps={{
                ...getInputProps(fields.price, {
                  type: 'number',
                }),
                defaultValue: '',
                value: fields.price.value,
                variant: 'outlined',
                size: '48px',
                leftIcon: <div>€</div>,
              }}
              errors={fields.price.errors}
            />
            {finalPrice && finalPrice !== parseFloat(fields.price.value) && (
              <div className="text-sm text-gray-600">
                Precio final con descuento: {formatPrice(finalPrice)}
              </div>
            )}
          </div>
          <div className="w-full">
            <Field
              labelProps={{ children: 'Precio financiación' }}
              inputProps={{
                ...getInputProps(fields.minFinancedAmount, {
                  type: 'number',
                }),
                value: fields.minFinancedAmount.value || '',
                variant: 'outlined',
                size: '48px',
                leftIcon: <div>€</div>,
              }}
              errors={fields.minFinancedAmount.errors}
            />
            {finalFinancePrice &&
              finalFinancePrice !==
                parseFloat(fields.minFinancedAmount.value) && (
                <div className="text-sm text-gray-600">
                  Precio final con descuento: {formatPrice(finalFinancePrice)}
                </div>
              )}
          </div>
        </div>
        <div className="flex gap-4">
          <Field
            labelProps={{ children: 'Descuento (%)' }}
            inputProps={{
              ...getInputProps(fields.discount, {
                type: 'number',
                min: 0,
                max: 100,
              }),
              defaultValue: '',
              value: fields.discount.value,
              variant: 'outlined',
              size: '48px',
              leftIcon: <div>%</div>,
              placeholder: 'Indica el porcentaje',
            }}
            errors={fields.discount.errors}
          />
          <Field
            labelProps={{ children: 'Interés actual (TIN %)' }}
            inputProps={{
              ...getInputProps(fields.annualInterestRate, {
                type: 'number',
                min: 0,
                max: 100,
                step: '0.01',
              }),
              defaultValue: DEFAULT_ANNUAL_INTEREST_RATE.toString(),
              value: fields.annualInterestRate.value,
              variant: 'outlined',
              size: '48px',
              leftIcon: <div>%</div>,
              placeholder: 'Ej: 8.50',
            }}
            errors={fields.annualInterestRate.errors}
          />
        </div>
        <div className="flex gap-4">
          <Field
            labelProps={{
              children: 'TIN Pack Óptima (%) - Para financiación ≥72 meses',
            }}
            inputProps={{
              ...getInputProps(fields.annualInterestRatePackGo, {
                type: 'number',
                min: 0,
                max: 100,
                step: '0.01',
              }),
              defaultValue: '',
              value: fields.annualInterestRatePackGo.value || '',
              variant: 'outlined',
              size: '48px',
              leftIcon: <div>%</div>,
              placeholder: 'Ej: 6.99',
            }}
            errors={fields.annualInterestRatePackGo.errors}
          />
        </div>

        {financing && finalPrice && (
          <div className="mt-4 rounded-lg bg-gray-50 p-4">
            <p className="text-sm text-gray-600">
              Financiación disponible desde:
            </p>
            <p className="text-xl font-semibold">
              {formatPrice(financing.maxTerm.monthlyPayment)}/mes
            </p>
            <p className="text-xs text-gray-500">
              *Calculado con {financing.maxTerm.tin}% de interés a *Calculado
              con {financing.maxTerm.tin}% de interés a {MAX_FINANCING_MONTHS}{' '}
              meses
            </p>
            <p className="text-xs text-gray-500">
              Precio base: {formatPrice(financing.basePrice)}
            </p>
            <p className="text-xs text-gray-500">
              Precio mínimo financiado ({MIN_FINANCING_MONTHS} meses):{' '}
              {formatPrice(financing.minTerm.totalAmount || 0)}
            </p>
            <p className="text-xs text-gray-500">
              Precio máximo financiado ({MAX_FINANCING_MONTHS} meses):{' '}
              {formatPrice(financing.maxTerm.totalAmount)}
            </p>
          </div>
        )}

        <Field
          labelProps={{ children: '' }}
          inputProps={{
            ...getInputProps(fields.minMonthlyPayment, {
              type: 'number',
            }),
            className: 'hidden',
            value: financing?.maxTerm.monthlyPayment || '', // Cuota mensual a 120 meses
          }}
        />
      </div>
    </Card>
  )
}
